import type { OnInit } from '@angular/core';
import type { Observable } from 'rxjs';
import type { SelectedFileInterface } from '@sendpulse/file-manager';
import type { SelectedFileExtended } from '@common/types/file.type';
import type { ReplacedFile } from '@components/sp-file-manager/src/types';
import type { WebLegacyWidgetEntity } from '@web/types';

import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, signal } from '@angular/core';
import { DirectoryItemCategory, FmSourceType } from '@sendpulse/file-manager';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

import { WidgetsDataTypes } from '@common/enums';
import { extendWebFileDataPromise } from '@common/helpers';
import { PreviewType } from '@components/sp-file-manager/src/types';
import { SpInputNumComponent } from '@components/sp-form-controls/sp-input-num/sp-input-num.component';
import { SpFileManagerComponentModule } from '@components/sp-file-manager/src/sp-file-manager.module';
import { SpControlHandleComponent } from '@ui/sp-control-handle';

import type { LogoEntity, SelectedFileLegacy } from '@sites/entity-core';
import type { LogoForm } from '@sites/entity-signal-form';

import { environment } from '@web-env/environment';
import { SiteFacade } from '@web/store/website/site/site.facade';
import { UserFacade } from '@web/store/user/user.facade';
import { WebLegacyWidgetSignalFormService } from '@web/core/services/web-legacy-widget-signal-form.service';
import { UseUnsplash } from '@web/shared/hooks/use-unsplash.hook';
import { WebSettingsModule } from '@web/shared/components/web-settings/web-settings.module';
import { WebCustomIdComponent } from '@web/shared/components/web-custom-id/web-custom-id.component';

import { ImageSettingsComponent } from './image-settings/image-settings.component';

/**
 * Standalone, `input()`-based control panel for the `logo` widget: pick a file,
 * cap its width, and open the trimmed alt/link sub-panel. The picked file lives in
 * `data`, the cap in the envelope's `styles` — both written through the shared
 * signal form.
 */
@Component({
    selector: 'web-logo-control',
    templateUrl: './logo-control.component.html',
    styleUrls: ['./logo-control.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        WebSettingsModule,
        SpFileManagerComponentModule,
        SpControlHandleComponent,
        TooltipModule,
        SpInputNumComponent,
        ImageSettingsComponent,
        TranslateModule,
        WebCustomIdComponent,
    ],
})
export class LogoControlComponent implements OnInit {
    public readonly widget = input.required<WebLegacyWidgetEntity<LogoEntity['data']>>();
    public readonly isSpecific = input<boolean>(false);
    public readonly isEduSite = input<boolean>(false);

    private readonly signalFormService: WebLegacyWidgetSignalFormService = inject(WebLegacyWidgetSignalFormService);
    private readonly siteFacade: SiteFacade = inject(SiteFacade);
    private readonly userFacade: UserFacade = inject(UserFacade);
    private readonly useUnsplash: UseUnsplash = inject(UseUnsplash);
    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    public readonly PreviewType = PreviewType;
    public readonly DirectoryItemCategory = DirectoryItemCategory;
    public readonly fmSourceType = FmSourceType;

    public readonly userId$: Observable<number> = this.userFacade.userId$;

    public readonly buttonDisabled = signal<boolean>(false);
    /** Snapshot of the picked file taken when the alt/link sub-panel opened; `null` while it is closed. */
    public readonly editableImage = signal<SelectedFileExtended | null>(null);

    public form!: LogoForm;

    public readonly image = computed<SelectedFileExtended | null>(
        () => this.form.image().value() as unknown as SelectedFileExtended | null
    );
    public readonly maxWidth = computed<number>(() => this.form.maxWidth().value());
    public readonly imageNaturalWidth = computed<number | undefined>(() => this.image()?.imageWidth);

    public readonly imageSubText = computed<string>(() => {
        const image = this.image();
        return image ? `${image.imageWidth}x${image.imageHeight}px` : '';
    });

    public ngOnInit(): void {
        this.form = this.signalFormService.getOrCreateScoped<LogoForm>(this.widget().id, this.destroyRef);
    }

    /**
     * Picking a file also resets the width cap to its natural width and asks the
     * backend for resized variants — both halves of what the FormGroup control did.
     * The two writes land as one history entry (synchronous writes coalesce).
     */
    public async setImage(file: SelectedFileInterface): Promise<void> {
        await this.applyPickedFile(file);
    }

    public async replaceImage({ file }: ReplacedFile): Promise<void> {
        await this.applyPickedFile(file);
    }

    public removeFile(): void {
        this.form.setImage(null);
    }

    public setMaxWidth(value: number | string): void {
        this.form.setMaxWidth(Number(value));
    }

    public openUnsplashModal(): void {
        this.useUnsplash.openUnsplashModal({ widgetId: this.widget().id, dataType: WidgetsDataTypes.image });
    }

    /**
     * Open (or close, when passed `null`) the alt/link sub-panel. On open we
     * snapshot the picked file straight from the live form — the signal form
     * mutates immutably, so this reference stays frozen at the pre-edit value and
     * lets {@link cancelImageSettings} restore it exactly.
     */
    public editImage(image: SelectedFileInterface | null): void {
        this.editableImage.set(image ? this.image() : null);
    }

    /** Revert the picked file to the snapshot taken when the sub-panel opened, then close it. */
    public cancelImageSettings(): void {
        const snapshot = this.editableImage();
        if (snapshot && this.form.image().value() !== (snapshot as unknown)) {
            this.form.setImage(snapshot as unknown as SelectedFileLegacy);
        }
        this.editImage(null);
    }

    private async applyPickedFile(file: SelectedFileInterface): Promise<void> {
        this.buttonDisabled.set(true);

        const extendedFile: SelectedFileExtended = await extendWebFileDataPromise(file, environment.fileManagerCdnUrl);

        this.form.setImage(extendedFile as unknown as SelectedFileLegacy);
        this.form.setMaxWidth(extendedFile.imageWidth);

        this.siteFacade.resizeImage({
            path: extendedFile.path,
            url: `${environment.fileManagerCdnUrl}${extendedFile.url}`,
        });

        this.buttonDisabled.set(false);
    }
}
